import React, {
  useState, useEffect, useMemo, useContext,
} from "react";
import { toast } from "react-toastify";
import {
  Modal,
  Button,
  Checkbox,
  Divider,
  Select,
  Form,
} from "antd";
import { getUser } from "../../../../../utils/helpers";
import { PendingTableCellContext } from "../../ChemBioPendingContext";

const DefaultInstrumentFormNameSuffix = "_instrument_checkbox_item";
const InstrumentModal = () => {
  const {
    showInstrumentModal,
    modalTest,
    modalMap,
    handleCloseModal,
    helperFn,
    mrlInstruments,
    mrlTestSet,
  } = useContext(PendingTableCellContext);

  const [form] = Form.useForm();
  const [selectedTests, setSelectedTests] = useState(new Map());
  const [dfOptions, setDfOptions] = useState([]);
  const info = useMemo(() => [
    { label: "Work order", param: "order_id" },
    { label: "Instrument", param: "instrument_name" },
    { label: "Sample Type", param: "sample_type" },
  ], []);

  const statusMap = useMemo(() => {
    const baseMap = {
      pending: { actionLabel: `Extraction done for ${modalTest?.genista_id}?`, actionParam: "extract" },
      extract: { actionLabel: `Loaded for ${modalTest?.genista_id}?`, actionParam: "load" },
    };
    // For metals category tests in load/analyze status, show InstrumentModal and move to analyze stage
    const isMetalsCategory = modalTest.test_category === "Metals";
    if (isMetalsCategory) {
      baseMap.load = { actionLabel: `Analyzed for ${modalTest?.genista_id}?`, actionParam: "analyze" };
      baseMap.analyze = { actionLabel: `${modalTest?.genista_id} is  already analyzed `, actionParam: "analyze" };
    }
    return baseMap;
  }, [modalTest]);

  const testList = useMemo(() => {
    // For metals category tests in load/analyze status, modalMap test only contains the data for current test,
    // so return the modalTest as the test list to render the checkbox
    const isMetalsCategory = modalTest.test_category === "Metals";
    const defaultList = modalMap.get(modalTest?.instrument_name).get(modalTest.genista_id).get(modalTest?.sample_status) ?? [];
    if (isMetalsCategory
      && modalTest
      && (modalTest.sample_status === "load" || modalTest.sample_status === "analyze")) {
      // For metals category, use the current modalTest
      return [modalTest];
    }
    return defaultList;
  }, [modalMap, modalTest]);

  const [loading, setLoading] = useState(false);
  const isValidPesticideItem = (item) => mrlTestSet.has(item.test) && item.sample_status === "extract";
  const [formFieldsData, setFormFieldsData] = useState({ checked: false, fieldsToValidate: [] });

  // Check if this is metals category in load/analyze status - disable test selection
  const isMetalsLoadAnalyze = useMemo(() => {
    const isMetalsCategory = modalTest.test_category === "Metals";
    return isMetalsCategory
           && (modalTest.sample_status === "load" || modalTest.sample_status === "analyze");
  }, [modalTest]);

  // Check if metals category is already in "analyze" status - hide submit button
  const isMetalsInAnalyze = useMemo(() => {
    const isMetalsCategory = modalTest.test_category === "Metals";
    return isMetalsCategory && modalTest.sample_status === "analyze";
  }, [modalTest]);

  useEffect(() => {
    // Set the default test checkbox value to true
    if (modalTest) {
      const { test_in_sample_id } = modalTest;
      setSelectedTests((prev) => {
        const newMap = new Map(prev);
        newMap.set(test_in_sample_id, modalTest);
        return newMap;
      });
    }
  }, [modalTest]);

  /**
   * Make an API call to update tests' status
   * @param {*} action "extract" or "load"
   */
  const handleMarkRunOnInstrument = async (action) => {
    const mapValues = Array.from(selectedTests.values()); // Get the list of test id from the selected tests map
    const checkedFields = mapValues.map((item) => item.test_in_sample_id);
    const instrumentFields = mapValues.map((item) => `${item.test_in_sample_id}${DefaultInstrumentFormNameSuffix}`);
    const formValues = await form.validateFields([...checkedFields, ...instrumentFields]);
    setLoading(true);
    const test_in_sample_details = mapValues.map((item) => {
      const { test_in_sample_id } = item;
      const id = test_in_sample_id;
      const loaded_on_instruments = isValidPesticideItem(item) ? formValues[`${test_in_sample_id}${DefaultInstrumentFormNameSuffix}`] : [];
      return { id, loaded_on_instruments };
    });
    const payload = {
      test_in_sample_details,
      action,
      user_id: getUser().id,
    };
    const { message, success } = await helperFn.markTestRunOnInstrument(payload);
    if (success) {
      handleCloseModal(true, message);
    } else {
      setLoading(false);
      toast.error(message);
    }
  };

  /**
   * Update the selectedTests map for a checkbox event
   */
  const updateSelectedTestsMap = (checked, newMap, item, fieldsToValidate) => {
    const { test_in_sample_id } = item;
    if (checked) {
      newMap.set(test_in_sample_id, item);
      if (modalTest?.enabledDfInput && form.getFieldValue(test_in_sample_id).length > 0) {
        fieldsToValidate.push(test_in_sample_id);
      }
      if (isValidPesticideItem(item) && form.getFieldValue(`${test_in_sample_id}${DefaultInstrumentFormNameSuffix}`).length > 0) {
        fieldsToValidate.push(`${test_in_sample_id}${DefaultInstrumentFormNameSuffix}`);
      }
    } else {
      newMap.delete(test_in_sample_id);
      fieldsToValidate.push(test_in_sample_id);
      if (isValidPesticideItem(item)) {
        fieldsToValidate.push(`${test_in_sample_id}${DefaultInstrumentFormNameSuffix}`);
      }
    }
    setFormFieldsData({ checked, fieldsToValidate });
  };

  /**
   * Trigger the form validation to render the latest form validation status on each checkbox event
   * @param {*} checked checkbox value
   * @param {*} fieldsToValidate dropdown name in the form
   */
  const validateFormOnCheckboxChange = (data) => {
    const { checked, fieldsToValidate } = data;
    // validate DF related forms
    if (!checked && modalTest.enabledDfInput) {
      // if it is a unchecked action, always validate the input to get the latest validation status
      form.validateFields(fieldsToValidate);
    } else if (modalTest.enabledDfInput && checked && fieldsToValidate.length > 0) {
      // if it is a checked action, validate the dropdown only when it has values
      form.validateFields(fieldsToValidate);
    }
    // validate instruments related forms
    if (!checked) {
      // if it is a unchecked action, always validate the input to get the latest validation status
      form.validateFields(fieldsToValidate);
    } else if (checked && fieldsToValidate.length > 0) {
      // if it is a checked action, validate the dropdown only when it has values
    }
    form.validateFields(fieldsToValidate);
  };

  /**
   * Put or remove the selected test id into the map
   */
  const onTestCheckboxChange = (e, item) => {
    const checked = e.target.checked;
    const fieldsToValidate = [];
    setSelectedTests((prev) => {
      const newMap = new Map(prev);
      updateSelectedTestsMap(checked, newMap, item, fieldsToValidate);
      return newMap;
    });
  };

  /**
     * Handle the select all checkbox event
     */
  const onTestCheckboxSelectAll = async (e, list) => {
    const checked = e.target.checked;
    const fieldsToValidate = [];
    setSelectedTests((prev) => {
      const newMap = new Map(prev);
      list.forEach((item) => {
        updateSelectedTestsMap(checked, newMap, item, fieldsToValidate);
      });
      return newMap;
    });
  };

  // Validate the form fields on checkbox event
  useEffect(() => {
    validateFormOnCheckboxChange(formFieldsData);
  }, [formFieldsData]); // eslint-disable-line

  const isValidPositiveInteger = (value) => !value.includes(".") && value !== "0" && /^\d+$/.test(value); // only accept positive integer

  const handleAddDfOptions = (value) => {
    if (isValidPositiveInteger(value) && !dfOptions.find((opt) => opt.value === value)) { // only add integer to the list
      setDfOptions((prevState) => {
        const newState = [...prevState];
        newState.push({ value });
        return newState;
      });
    }
  };

  const validateInteger = (data, listOfValues) => {
    const testId = parseInt(data.field);
    if (!selectedTests.has(testId) || !listOfValues || listOfValues.length === 0 || listOfValues.every((value) => isValidPositiveInteger(value))) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Please enter positive integer"));
  };

  const onAfterClose = () => {
    form.resetFields();
    setSelectedTests(new Map());
    setDfOptions([]);
  };

  return (
    <Modal
      centered
      destroyOnClose
      title={statusMap[modalTest?.sample_status]?.actionLabel}
      open={showInstrumentModal}
      closable={false}
      maskClosable={false}
      width="800px"
      afterClose={onAfterClose}
      footer={[
        <Button key="close" onClick={() => handleCloseModal(false)} disabled={loading}>
          Close
        </Button>,
        ...(!isMetalsInAnalyze ? [
          <Button
            key="submit"
            type="primary"
            disabled={selectedTests.size === 0}
            loading={loading}
            onClick={() => handleMarkRunOnInstrument(statusMap[modalTest?.sample_status]?.actionParam)}
          >
            Submit
          </Button>,
        ] : []),
      ]}
    >
      <div>
        <div className="PendingTable__InstrumentModal__Info">
          {info.map((item, i) => (
            <div key={i} className="PendingTable__InstrumentModal__Info_Content">
              <label>
                {`${item.label}:`}
              </label>
              <span>{modalTest?.[item.param]}</span>
            </div>
          ))}
        </div>
        <div className="PendingTable__CheckboxContainer">
          {!isMetalsLoadAnalyze && (
            <>
              <Checkbox
                className="PendingTable__Checkbox--selectAll"
                onChange={(e) => onTestCheckboxSelectAll(e, testList)}
                checked={selectedTests.size === testList.length}
                disabled={loading}
              >
                Select All
              </Checkbox>
              <Divider className="PendingTable__CheckboxContainer__Divider" />
            </>
          )}
          <Form form={form} className="PendingTable__CheckboxContainer__Tests">
            {testList.map((testItem, checkboxKey) => {
              const {
                test, test_in_sample_id: eachTestId, dilution_factors_list,
              } = testItem;
              return (
                <div key={checkboxKey} className="PendingTable__TestCard">
                  <Checkbox
                    onChange={(e) => onTestCheckboxChange(e, testItem)}
                    checked={selectedTests.has(eachTestId)}
                    disabled={loading || isMetalsLoadAnalyze}
                  >
                    {test}
                  </Checkbox>
                  {isValidPesticideItem(testItem) && (
                  <Form.Item
                    preserve={false}
                    className="ChemPesticidesTable__FormItem"
                    name={`${eachTestId}${DefaultInstrumentFormNameSuffix}`}
                    initialValue={[]}
                    rules={[
                      {
                        required: selectedTests.has(eachTestId),
                        message: "Please select an instrument",
                      },
                    ]}
                  >
                    <Checkbox.Group disabled={!selectedTests.has(eachTestId)}>
                      {mrlInstruments.map((instrumentItem, instrumentIdx) => (
                        <Checkbox
                          key={instrumentIdx}
                          value={instrumentItem.id}
                          disabled={loading}
                        >
                          {instrumentItem.instrument_name}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                  )}
                  {modalTest?.enabledDfInput && (
                    <Form.Item
                      name={eachTestId}
                      initialValue={dilution_factors_list}
                      rules={[
                        {
                          required: selectedTests.has(eachTestId),
                          message: "Please select a DF value",
                        },
                        { validator: validateInteger },
                      ]}
                      className="PendingTable__DdFormItem"
                    >
                      <Select
                        allowClear
                        mode="tags"
                        placeholder="DF Values"
                        className="PendingTable__InstrumentModal__DFSelect"
                        disabled={!selectedTests.has(eachTestId) || loading}
                        options={dfOptions}
                        onSelect={handleAddDfOptions}
                      />
                    </Form.Item>
                  )}
                </div>
              );
            })}
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default InstrumentModal;
